import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, useParams } from "react-router";
import { ArticlePage } from "../pages/article";
import { CategoryPage } from "../pages/category";
import { DocsIndexPage } from "../pages/docs-index";
import { HomePage } from "../pages/home";
import { NotFoundPage } from "../pages/not-found";
import { isProductId } from "../shared/lib/products";
import { LoadingState } from "../shared/ui/states";
import { DocsShell } from "../widgets/docs-shell";
import { DocsProvider, productForHost } from "./docs-context";

// The console — editor, media library, analytics — is lazy so that readers, who are almost all the
// traffic, never download the authoring code.
const ConsoleApp = lazy(() =>
  import("../pages/console/console-app").then((m) => ({ default: m.ConsoleApp })),
);
const AdminApp = lazy(() =>
  import("../pages/admin/admin-app").then((m) => ({ default: m.AdminApp })),
);
const LoginRoute = lazy(() =>
  import("../pages/login-route").then((m) => ({ default: m.LoginRoute })),
);

/**
 * One SPA, three areas (ADR-0046 decision 1):
 *
 * - the documentation reader. On a product's docs hostname (listed in `env.js`) it owns the whole
 *   site: `/`, `/category/:slug`, `/docs/:slug`. On any other hostname — the console's, localhost —
 *   the product comes from the path: `/docs/:product`, `/docs/:product/category/:slug`,
 *   `/docs/:product/:slug`.
 * - the KB authoring console at `/admin/*`, and
 * - the ticket console at `/console/*` (package B3), and
 * - `/login`, where a refused RigoHR handoff and a sign-out land (ADR-0059).
 *
 * The consoles exist only off a docs hostname: a docs hostname routes `/kb` to the host and nothing
 * else, so a console there would have no `/api` and no `/bff` to call.
 */
export function AppRoutes({ hostname = window.location.hostname }: { hostname?: string }) {
  return (
    <BrowserRouter>
      <AppRouteTable hostname={hostname} />
    </BrowserRouter>
  );
}

/** The route table without the router, so a test can mount it under a MemoryRouter. */
export function AppRouteTable({ hostname }: { hostname: string }) {
  const hostProduct = productForHost(hostname);

  if (hostProduct) {
    return (
      <Routes>
        <Route
          element={
            <DocsProvider mode="host" product={hostProduct}>
              <DocsShell />
            </DocsProvider>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="category/:slug" element={<CategoryPage />} />
          <Route path="docs/:slug" element={<ArticlePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route index element={<DocsIndexPage />} />

      <Route path="docs/:product" element={<PrefixDocs />}>
        <Route index element={<HomePage />} />
        <Route path="category/:slug" element={<CategoryPage />} />
        <Route path=":slug" element={<ArticlePage />} />
      </Route>

      <Route
        path="admin/*"
        element={
          <Suspense fallback={<LoadingState label="Loading console…" />}>
            <AdminApp />
          </Suspense>
        }
      />

      {/* The ticket console (package B3), moved from RigoHR's hrms-support portal half. */}
      <Route
        path="console/*"
        element={
          <Suspense fallback={<LoadingState label="Loading console…" />}>
            <ConsoleApp />
          </Suspense>
        }
      />

      <Route
        path="login"
        element={
          <Suspense fallback={<LoadingState label="Loading console…" />}>
            <LoginRoute />
          </Suspense>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/** `/docs/:product/*`: the reader, with the product taken from the path. */
function PrefixDocs() {
  const { product } = useParams();
  if (!isProductId(product)) return <NotFoundPage />;

  // Keyed by product, so moving between products starts the shell (nav, search) fresh.
  return (
    <DocsProvider key={product} mode="prefix" product={product}>
      <DocsShell />
    </DocsProvider>
  );
}
